import { MatchParams } from 'react-resource-router';

import {
  ARTIFACTS_PAGE_LENGTH,
  CONTAINERS_IMAGE_TAGS_PAGE_LENGTH,
  CONTAINERS_IMAGES_PAGE_LENGTH,
  PACKAGE_TYPE_QUERY_PARAM,
  PACKAGES_PAGE_LENGTH,
  PAGE_QUERY_PARAM,
  PAGELEN_QUERY_PARAM,
  REGISTRY_HOSTNAME,
  SEARCH_MAVEN_BASE_VERSION_QUERY_PARAM,
  SEARCH_PACKAGE_NAME_QUERY_PARAM,
  SEARCH_TAG_NAME_QUERY_PARAM,
  SEARCH_VERSION_QUERY_PARAM,
  SORT_QUERY_PARAM,
} from './constants';
import { PackagesContext } from './types';

// map of frontend sort keys to their backend equivalent
const SORT_KEY_MAP: Record<string, string> = {
  name: 'name',
  updated_on: 'updatedAt',
};

/**
 * Determine the context of a packages page based on the URL path parameters
 * @param params Path parameters from the router
 * @returns a PackagesContext object containing the container
 * (workspace/project/repository) and relevant path parameters
 */
export const getPackagesContext = (params: MatchParams): PackagesContext => {
  const { workspaceSlug, projectKey, repositoryOwner, repositorySlug } = params;
  if (repositoryOwner && repositorySlug) {
    return {
      container: 'repository',
      workspaceSlug: repositoryOwner,
      repositorySlug,
    };
    // We explicitly check for 'projectKey' in the map to avoid falling back to
    // a workspace context if projectKey is present but falsy
  } else if ('workspaceSlug' in params && 'projectKey' in params) {
    if (workspaceSlug && projectKey) {
      return {
        container: 'project',
        workspaceSlug,
        projectKey,
      };
    }
  } else if (workspaceSlug) {
    return {
      container: 'workspace',
      workspaceSlug,
    };
  }

  throw new Error('Unable to determine packages context from route path');
};

/**
 * This function translates a frontend `sort` param to the backend format.
 *
 * The `sort` query param we display to the user in the frontend isn't
 * understood by the packages backend, so we need to translate it to the
 * correct format.
 *
 * The `sort` param in the frontend:
 *  - uses the **API response** field name
 *  - uses a `-` prefix to denote a descending sort order
 *  - examples: `-name`, `updated_on`
 *
 * Whereas the `sort` param in the backend:
 *  - uses the field name of the **DB entity** being queried
 *  - uses `asc|desc` to denote sort order
 *  - examples: `name,desc`, `updated_on,asc`
 *
 * @param param the `sort` param to translate
 * @returns the translated `sort` param, or an empty string if `param` has no
 * valid translation
 */
export const translateSortParam = (param: string): string => {
  if (!param) {
    return '';
  }

  const sortDirection = param.startsWith('-') ? 'desc' : 'asc';
  const clientSortKey = param.startsWith('-') ? param.substring(1) : param;
  const apiSortKey = SORT_KEY_MAP[clientSortKey];
  if (apiSortKey === undefined) {
    return '';
  }

  return `${apiSortKey},${sortDirection}`;
};

/**
 * Generate a query parameter string for a request to the Packages API. Handles
 * translation of query parameters as necessary.
 * @param params the query parameters to include in the query string
 * @returns a query string, e.g. a=123&b=456
 */
export const buildPackagesApiQueryString = (params: {
  [key: string]: string | undefined;
}): string => {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      if (key === SORT_QUERY_PARAM) {
        q.append(key, translateSortParam(value));
      } else {
        q.append(key, value);
      }
    }
  }
  return q.toString();
};

export const buildQueryString = (params: {
  [key: string]: string | undefined;
}): string => {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      q.append(key, value);
    }
  }
  return q.toString();
};

export const buildContainerImagesQueryString = (
  sortQueryParam: string | undefined,
  pageQueryParam: string | undefined,
  nameQueryParam: string | undefined
): string => {
  return buildQueryString({
    [PAGELEN_QUERY_PARAM]: CONTAINERS_IMAGES_PAGE_LENGTH.toString(),
    [SORT_QUERY_PARAM]: sortQueryParam,
    [PAGE_QUERY_PARAM]: pageQueryParam,
    [SEARCH_PACKAGE_NAME_QUERY_PARAM]: nameQueryParam,
  });
};

export const buildPackagesQueryString = (
  sortQueryParam: string | undefined,
  pageQueryParam: string | undefined,
  nameQueryParam: string | undefined,
  typeQueryParam: string | undefined
): string => {
  return buildQueryString({
    [PAGELEN_QUERY_PARAM]: PACKAGES_PAGE_LENGTH.toString(),
    [SORT_QUERY_PARAM]: sortQueryParam,
    [PAGE_QUERY_PARAM]: pageQueryParam,
    [SEARCH_PACKAGE_NAME_QUERY_PARAM]: nameQueryParam,
    [PACKAGE_TYPE_QUERY_PARAM]: typeQueryParam,
  });
};

export const buildContainerImageTagsQueryString = (
  imageName: string,
  sortQueryParam: string | undefined,
  pageQueryParam: string | undefined,
  tagNameQueryParam: string | undefined
): string => {
  return buildQueryString({
    imageName: decodeURIComponent(imageName),
    [PAGELEN_QUERY_PARAM]: CONTAINERS_IMAGE_TAGS_PAGE_LENGTH.toString(),
    [SORT_QUERY_PARAM]: sortQueryParam,
    [PAGE_QUERY_PARAM]: pageQueryParam,
    [SEARCH_TAG_NAME_QUERY_PARAM]: tagNameQueryParam,
  });
};

export const buildContainerImageTagQueryString = (
  imageName: string,
  tagName: string,
  dryRun?: boolean
): string => {
  return buildQueryString({
    imageName: decodeURIComponent(imageName),
    tagName,
    ...(dryRun ? { dryRun: 'true' } : {}),
  });
};

export const buildContainerImageQueryString = (imageName: string): string => {
  return buildQueryString({
    imageName: decodeURIComponent(imageName),
  });
};

// Expects an already-decoded packageName (not URL-encoded)
export const buildMavenPackageQueryString = (packageName: string): string => {
  return buildQueryString({
    packageName,
  });
};

export const buildMavenBaseVersionsQueryString = (
  packageName: string,
  sortQueryParam: string | undefined,
  pageQueryParam: string | undefined,
  baseVersionQueryParam: string | undefined
): string => {
  return buildQueryString({
    packageName: decodeURIComponent(packageName),
    [PAGELEN_QUERY_PARAM]: ARTIFACTS_PAGE_LENGTH.toString(),
    [SORT_QUERY_PARAM]: sortQueryParam,
    [PAGE_QUERY_PARAM]: pageQueryParam,
    [SEARCH_MAVEN_BASE_VERSION_QUERY_PARAM]: baseVersionQueryParam,
  });
};

export const buildMavenBaseVersionQueryString = (
  packageName: string,
  baseVersion: string
): string => {
  return buildQueryString({
    packageName: decodeURIComponent(packageName),
    baseVersion,
  });
};

// Expects an already-decoded packageName (not URL-encoded)
export const buildMavenVersionQueryString = (
  packageName: string,
  baseVersion: string,
  version: string
): string => {
  return buildQueryString({
    packageName,
    baseVersion,
    version,
  });
};

export const buildMavenVersionsQueryString = (
  packageName: string,
  baseVersion: string,
  sortQueryParam: string | undefined,
  pageQueryParam: string | undefined
): string => {
  return buildQueryString({
    packageName: decodeURIComponent(packageName),
    baseVersion,
    [PAGELEN_QUERY_PARAM]: ARTIFACTS_PAGE_LENGTH.toString(),
    [SORT_QUERY_PARAM]: sortQueryParam,
    [PAGE_QUERY_PARAM]: pageQueryParam,
  });
};

export const buildNpmPackageQueryString = (packageName: string): string => {
  return buildQueryString({
    packageName: decodeURIComponent(packageName),
  });
};

export const buildVersionQueryString = (
  packageName: string,
  versionName: string
): string => {
  return buildQueryString({
    packageName: decodeURIComponent(packageName),
    [SEARCH_VERSION_QUERY_PARAM]: versionName,
  });
};

export const buildNpmVersionsQueryString = (
  packageName: string,
  sortQueryParam: string | undefined,
  pageQueryParam: string | undefined,
  versionQueryParam: string | undefined
): string => {
  return buildQueryString({
    packageName: decodeURIComponent(packageName),
    [PAGELEN_QUERY_PARAM]: ARTIFACTS_PAGE_LENGTH.toString(),
    [SORT_QUERY_PARAM]: sortQueryParam,
    [PAGE_QUERY_PARAM]: pageQueryParam,
    [SEARCH_VERSION_QUERY_PARAM]: versionQueryParam,
  });
};

export const getPullCommandText = (
  workspaceSlug: string,
  imageName: string,
  tagName: string
): string => {
  return `docker pull ${REGISTRY_HOSTNAME}/${workspaceSlug}/${imageName}:${tagName}`;
};
